// A one-line message the sign-in screen shows above the form, handed over by
// the callback when a provider sign-in (Microsoft) comes back with an error.
// Plain words only: the raw provider error is never shown to the person
// (customer-front-door doc, section 5).

export interface SignInProblem {
  error: string | null
  description: string | null
}

let pending: SignInProblem | null = null

/** Called by the callback before it sends the person back to the entry screen. */
export function setSignInNotice(problem: SignInProblem): void {
  pending = problem
}

/** Read without clearing (safe inside a React state initialiser). */
export function peekSignInNotice(): SignInProblem | null {
  return pending
}

/** Clear once shown. */
export function clearSignInNotice(): void {
  pending = null
}

/**
 * Turn a provider's error (`?error=…&error_description=…` on the callback,
 * or a failed code exchange) into a plain, branded sentence. `name` is how
 * the entry screen names the product ("Nateva Intelligence").
 */
export function providerErrorMessage(
  error: string | null,
  description: string | null,
  name: string,
): string {
  const text = `${error ?? ''} ${description ?? ''}`.toLowerCase()
  if (/sign ?ups? not allowed|signup_disabled|user not found|not allowed/.test(text)) {
    return `Your Microsoft account is not set up for ${name} yet. Sign in with your work email below, or ask your administrator for access.`
  }
  if (/cancel|consent|access_denied/.test(text)) {
    return 'Microsoft sign-in was cancelled. Try again, or sign in with your work email below.'
  }
  if (/verifier|code/.test(text)) {
    return 'Microsoft sign-in has to finish in the same browser it started in. Try again here, or sign in with your work email below.'
  }
  return 'Microsoft sign-in did not work this time. Sign in with your work email below.'
}
